// Screen 22 · Proposal — the generated view.
// Answers: what is the client being offered, at what price, with which choices?
// It has no state of its own: published Builds are the prices, the base first; under each the packages published
// under that Build (a bid with the package's add-ons in, at the margin typed on its cell), then every published Add-on
// that applies to that Build, priced against it at the margin typed on its row (Spencer, 2026-10-17 and 2026-10-22).
// A flip on the Price grid is the only way it changes.

import { addOnCell, gridCell } from '@zerogap/engine';
import { contactName, longDate, today } from '../../lib/format';
import { pricedUnder } from '../../lib/job';
import { useRows } from '../../lib/api';
import { Btn, Empty, Money, Tag } from '../../ui/kit';
import type { JobCtx } from './Workspace';

export function Proposal({ ctx }: { ctx: JobCtx }) {
  const { data, r } = ctx;
  const base = r.res!;
  const lib = r.lib!;
  const contact = useRows('contact', { eq: { id: data.opportunity?.primary_contact_id }, enabled: !!data.opportunity?.primary_contact_id }).rows[0];
  // the scope lines that apply to the whole job are part of what is included (2026-10-18)
  const scopeRows = useRows('scope_row', { eq: { opportunity_id: data.job?.opportunity_id }, order: ['sort', 'created_at'], enabled: !!data.job?.opportunity_id });
  const jobWide = scopeRows.rows.filter((x) => x.outcome === 'job');
  const published = data.builds.filter((b) => b.published);
  // the base leads, then the others in their order
  const builds = [...published.filter((b) => b.is_base), ...published.filter((b) => !b.is_base)];
  const addOns = data.addOns.filter((a) => a.published);
  // the packages: a bid with some add-ons rolled in, published per build on the Price grid (2026-10-22)
  // a retired package counts nowhere (2026-10-29)
  const packages = useRows('package', { eq: { job_id: data.job?.id }, order: ['sort', 'created_at'], enabled: !!data.job?.id }).rows.filter((p) => !p.retired_at);
  const appliesTo = (row: Record<string, any>, buildId: string) => {
    const to = (row.applies_to ?? []) as string[];
    return to.length === 0 || to.includes(buildId);
  };
  const marginOn = (row: Record<string, any>, buildId: string) => ((row.pricing_overrides ?? {}) as Record<string, { gross_margin_pct?: number | string }>)[buildId]?.gross_margin_pct ?? null;

  // what the client is told each item is: its catalog sentence where one exists, its name otherwise
  const lines = base.items
    .filter((i) => !i.parent_item_id)
    .map((i) => {
      const cat = i.catalog_item_id ? lib.catalog[i.catalog_item_id] : null;
      const kids = base.items.filter((k) => k.parent_item_id === i.id);
      return { item: i, text: i.name || cat?.name || i.number, kids };
    });

  return (
    <div>
      <div className="row mid noprint" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <span>
          <Tag>Preview</Tag> <small>Generated from the job. Nothing is edited here; Spencer sends it.</small>
        </span>
        <Btn read onClick={() => window.print()}>Print or save as PDF</Btn>
      </div>
      <div className="proposal">
        <div className="row mid" style={{ justifyContent: 'space-between', borderBottom: '2px solid var(--navy)', paddingBottom: 12 }}>
          <div>
            {/* the page's one h1 is the job's; this is the document's title, drawn at the same size */}
            <h2 style={{ fontSize: 20, letterSpacing: '.02em' }}>Urban Grain Cabinetry</h2>
            <small>Proposal · {longDate(today())}</small>
          </div>
          <div className="right">
            <b>{data.opportunity?.name}</b>
            <br />
            <small>
              {data.job?.scope_name}
              {contact ? ` · prepared for ${contactName(contact)}` : ''}
            </small>
          </div>
        </div>

        <h2 style={{ margin: '20px 0 8px' }}>What is included</h2>
        {lines.length === 0 && <Empty>No items on this job yet.</Empty>}
        {lines.map(({ item, text, kids }) => (
          <div key={item.id} className="ln">
            <span>
              <b>{item.number}</b> · {text}
              {kids.length > 0 && <small> · with {kids.length} {kids.length === 1 ? (kids[0]!.catalog_item_name ?? 'part').toLowerCase() : `${(kids[0]!.catalog_item_name ?? 'part').toLowerCase()}s`}</small>}
            </span>
          </div>
        ))}
        {data.subLines.map((l) => (
          <div key={l.id} className="ln">
            <span>{l.scope_text}</span>
          </div>
        ))}
        {/* the scope lines that apply to the whole job (a build, a standard, hardware across the job; 2026-10-18) */}
        {jobWide.map((x) => (
          <div key={x.id} className="ln">
            <span>{x.request_text}</span>
          </div>
        ))}

        <h2 style={{ margin: '24px 0 8px' }}>Price</h2>
        {builds.length === 0 && <Empty>No build is published yet. Publish one on the Price grid.</Empty>}
        {builds.map((b) => {
          const res = pricedUnder(r, b, []);
          return (
            <div key={b.id} style={{ marginBottom: 14 }}>
              <div className="ln">
                <span>
                  <b>{b.name}</b>
                </span>
                <span className="big">
                  <Money cents={res?.totals.price_cents} />
                </span>
              </div>
              {packages
                .filter((p) => appliesTo(p, b.id) && ((p.published ?? {}) as Record<string, boolean>)[b.id])
                .map((p) => {
                  // the bid with the package's add-ons in, at the margin typed on its cell
                  const on = ((p.add_on_ids ?? []) as string[]).map((id) => data.addOns.find((a) => a.id === id)).filter((a): a is NonNullable<typeof a> => !!a && appliesTo(a, b.id));
                  const withPkg = pricedUnder(r, b, on);
                  return (
                    <div key={p.id} className="ln sub">
                      <span>
                        {p.name}
                        {on.length > 0 && <small> · with {on.map((a) => a.name).join(', ')}</small>}
                      </span>
                      <span>{withPkg ? <Money cents={gridCell(withPkg.totals, marginOn(p, b.id)).price_cents} /> : '—'}</span>
                    </div>
                  );
                })}
              {addOns.filter((a) => appliesTo(a, b.id)).map((a) => {
                // each option priced against this Build: what it adds to this price (2026-10-17), at the margin typed on its row
                const withIt = res ? pricedUnder(r, b, [a]) : null;
                // what the option brings: the items it holds, by number (an alternate in place of the base item)
                const held = data.items.filter((i) => i.add_on_id === a.id && !i.parent_item_id);
                return (
                  <div key={a.id} className="ln sub">
                    <span>
                      Option · {a.name}
                      {held.length > 0 && <small> · {held.map((i) => i.number).join(', ')}</small>}
                    </span>
                    <span>{withIt && res ? <Money cents={addOnCell(withIt.totals, res.totals, marginOn(a, b.id)).price_cents} sign /> : '—'}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
        <small>Prices are for the scope listed above.</small>
      </div>
    </div>
  );
}
